import * as THREE from 'three';
import {
  buildCeiling,
  buildFloor,
  buildPerimeterWalls,
  resolveSpawnExit,
} from './interiorShell.js';
import { getInteriorDesign } from './interiorDesigns.js';

/**
 * Builds one complete interior from a design preset + shared room shell.
 */
export class InteriorTemplate {
  /**
   * @param {{
   *   designIndex: number;
   *   buildingMaterial: import('../../rendering/binary/BinaryMaterial.js').BinaryMaterial;
   *   accentMaterial: import('../../rendering/binary/BinaryMaterial.js').BinaryMaterial;
   *   seed: number;
   *   cellIdBase: number;
   *   origin: { x: number; y: number; z: number };
   * }} options
   */
  constructor({
    designIndex,
    buildingMaterial,
    accentMaterial,
    seed,
    cellIdBase,
    origin,
  }) {
    const design = getInteriorDesign(designIndex);
    this.designName = design.name;
    this.origin = origin;
    this.room = {
      width: design.width,
      depth: design.depth,
      height: design.height,
    };

    this.group = new THREE.Group();
    this.group.name = `Interior_${design.id}`;
    this.group.position.set(origin.x, origin.y, origin.z);

    /** @type {import('../Colliders.js').BoxCollider[]} */
    this.colliders = [];
    /** @type {import('../city/DigitalSign.js').DigitalSign[]} */
    this._signs = [];

    /** @type {import('./interiorShell.js').InteriorBuildContext} */
    const ctx = {
      group: this.group,
      buildingMaterial,
      accentMaterial,
      seed,
      cellIdBase,
      w: design.width,
      d: design.depth,
      h: design.height,
      colliders: this.colliders,
      signs: this._signs,
    };

    buildFloor(ctx);
    buildPerimeterWalls(ctx);
    buildCeiling(ctx);
    design.build(ctx);
    this.cellIdBase = ctx.cellIdBase;

    const { spawnPosition, exitPosition } = resolveSpawnExit(
      ctx,
      design.spawnLocal,
      origin,
    );
    this.spawnPosition = spawnPosition;
    this.exitPosition = exitPosition;
  }

  /** Colliders in world space. */
  getWorldColliders() {
    const ox = this.origin.x;
    const oy = this.origin.y;
    const oz = this.origin.z;
    return this.colliders.map((box) => ({
      min: new THREE.Vector3(box.min.x + ox, box.min.y + oy, box.min.z + oz),
      max: new THREE.Vector3(box.max.x + ox, box.max.y + oy, box.max.z + oz),
      isFloor: box.isFloor,
    }));
  }

  dispose() {
    for (const sign of this._signs) sign.dispose();
    this._signs = [];
    this.group.clear();
  }
}
